/**
 * The keyboard shortcuts the editor actually has, and the `?` key that reveals them.
 *
 * The list below is the whole list. It is kept next to the code that implements
 * the shortcuts (`CellRow.tsx` for the cell moves) rather than in a help page, so
 * that a shortcut cannot be documented that does not exist, or exist without being
 * documented -- the test in `ShortcutsDialog.test.tsx` pins that the list is
 * reachable from the keyboard, and reading `CellRow` pins that it is true.
 */
import { Fragment, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface Shortcut {
  /** Key names as printed on a keyboard. Never translated. */
  readonly keys: readonly string[]
  readonly labelKey: string
}

/** Every shortcut the editor implements. `Alt+arrow` lives in CellRow. */
export const SHORTCUTS: readonly Shortcut[] = [
  { keys: ['Alt', '↑'], labelKey: 'sheets.shortcuts.move_up' },
  { keys: ['Alt', '↓'], labelKey: 'sheets.shortcuts.move_down' },
  { keys: ['?'], labelKey: 'sheets.shortcuts.help' },
  { keys: ['Esc'], labelKey: 'sheets.shortcuts.close' },
]

/**
 * Whether a key event came from somewhere that is typing. `?` in a math field is
 * a character, not a command; MathLive's shadow DOM retargets its events to the
 * `<math-field>` host, which `closest` sees.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  return target.closest('input, textarea, select, math-field, [contenteditable]') !== null
}

/** Opens the list on `?` whenever focus is not in a field. */
export function useShortcutsHotkey(open: () => void): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '?' || event.ctrlKey || event.metaKey || event.altKey) return
      if (isTypingTarget(event.target)) return
      event.preventDefault()
      open()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])
}

export function ShortcutsDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('sheets')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('sheets.shortcuts.title')}</DialogTitle>
          <DialogDescription>{t('sheets.shortcuts.intro')}</DialogDescription>
        </DialogHeader>

        <dl className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 text-sm">
          {SHORTCUTS.map((shortcut) => (
            <Fragment key={shortcut.labelKey}>
              <dt className="flex items-center gap-1">
                {shortcut.keys.map((key, index) => (
                  <Fragment key={key}>
                    {index > 0 && (
                      <span className="text-text-muted" aria-hidden>
                        +
                      </span>
                    )}
                    <kbd className="rounded border border-border bg-surface-raised px-1.5 py-0.5 font-mono text-xs text-text">
                      {key}
                    </kbd>
                  </Fragment>
                ))}
              </dt>
              <dd className="m-0 text-text">{t(shortcut.labelKey)}</dd>
            </Fragment>
          ))}
        </dl>

        <p className="mt-4 text-xs leading-relaxed text-text-muted">
          {t('sheets.shortcuts.move_note')}
        </p>
      </DialogContent>
    </Dialog>
  )
}
